/**
 * Phase 16 — Hook to read a ticket's latest commercial closeout snapshot.
 *
 * Fetches the highest-version row for the ticket. The hash is verified
 * inline (one of the three scoped contexts where verification runs).
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface CommercialCloseoutRow {
  id: string;
  ticket_id: string;
  shop_id: string;
  version: number;
  supersedes_id: string | null;
  estimated_total: number | null;
  approved_total: number | null;
  performed_total: number | null;
  sold_total: number | null;
  invoiced_total: number | null;
  paid_total: number | null;
  refunded_total: number | null;
  parts_breakdown: Record<string, unknown>;
  labor_breakdown: Record<string, unknown>;
  fees_breakdown: Record<string, unknown>;
  gp_dollars: number | null;
  gp_percent: number | null;
  tech_pay_total: number | null;
  effective_margin: number | null;
  closeout_hash: string;
  variance_reason: string | null;
  closed_at: string;
  closed_by: string | null;
}

export function useTicketCommercialCloseout(ticketId?: string | null) {
  return useQuery({
    queryKey: ['ticket-commercial-closeout', ticketId],
    enabled: !!ticketId,
    queryFn: async (): Promise<CommercialCloseoutRow | null> => {
      const { data, error } = await (supabase as any)
        .from('ticket_commercial_closeout')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('version', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as CommercialCloseoutRow | null;
    },
  });
}
